import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_ERROR } from 'final-form';
import CreateAccount, { Props as CreateAccountProps } from '../CreateAccount';
import AgreementCheckboxWrapper from '../../../../../../AgreementCheckboxWrapper';
import Link from '../../../../../../Link';
import { useConfig } from '../../../../../../context';
import { NewFfpAccountParameters } from '@websky/graphql';
import { useTheme } from '../../../../../../theme';

const CreateAccountWithCheckbox: React.FC<CreateAccountProps> = ({ checkboxes: initialCheckboxes, ...props }) => {
	const { t } = useTranslation('Account');
	const theme = useTheme('Loyalty').LoyaltyConnect;

	const companyInfo = useConfig()?.global.companyInfo;

	const [showTooltip, setShowTooltip] = useState(false);

	const checkboxes = React.useMemo(() => {
		if (initialCheckboxes?.length > 0) {
			return initialCheckboxes;
		}

		const links: React.JSX.Element[] = [];

		const exarePrivacyPolicyURL = companyInfo?.exarePrivacyPolicyURL;
		const loyaltyProgramRulesUrl = companyInfo?.loyaltyProgramRulesUrl;

		if (exarePrivacyPolicyURL) {
			links.push(
				<Link target={'_blank'} action={exarePrivacyPolicyURL}>
					{t('CreateAccountCheckbox_exarePrivacyPolicy')}
				</Link>
			);
		}

		if (loyaltyProgramRulesUrl) {
			links.push(
				<Link target={'_blank'} action={loyaltyProgramRulesUrl}>
					{t('CreateAccountCheckbox_loyaltyStatus')}
				</Link>
			);
		}

		if (links.length === 0) {
			return [];
		}

		return [
			{
				tooltipTitle: t('CreateAccountCheckbox_tooltipTitle'),
				fieldName: 'policy',
				label: (
					<>
						<span>
							{t('CreateAccountCheckbox_agreeWith')} {links[0]}
						</span>
						{!!links[1] && (
							<span>
								{` ${t('CreateAccountCheckbox_and')} `} {links[1]}
							</span>
						)}
					</>
				),
				required: true
			}
		];
	}, [companyInfo, initialCheckboxes, t]);

	const handleSubmitButtonClick = (isValid: boolean) => {
		setShowTooltip(false);

		if (!isValid) {
			setShowTooltip(true);
		}
	};

	const handleCreateAccount = async (
		params: NewFfpAccountParameters[],
		isValid: boolean,
		invalidCallback: () => void
	): Promise<Object> => {
		if (!isValid) {
			invalidCallback?.();

			return { [FORM_ERROR]: '' };
		}

		return props.onCreateAccount(params);
	};

	if (checkboxes.length === 0) {
		return <CreateAccount {...props} />;
	}

	return (
		<AgreementCheckboxWrapper
			className={theme.agreementCheckboxWrapper}
			showTooltip={showTooltip}
			checkboxes={checkboxes}
		>
			{({ isValid, scrollToCheckboxes }) => (
				<CreateAccount
					{...props}
					onSubmitForm={() => handleSubmitButtonClick(isValid)}
					onCreateAccount={params => handleCreateAccount(params, isValid, scrollToCheckboxes)}
				/>
			)}
		</AgreementCheckboxWrapper>
	);
};

export default CreateAccountWithCheckbox;
